import { accountUserProfileService } from '../../services/accountUserProfileService';
import { getLearningSequenceSettings } from '../../services/reviewService';

/** 与 AppFunctor resolveContentAgeRange 对齐：区间 [A+H-1, A+H+2] */
export function resolveContentAgeRange(
  learnerAgeYears: number,
  contentAgeAheadYears = 1,
): { contentAgeMin: number; contentAgeMax: number; contentAgeAheadYears: number } {
  const ahead = Math.min(10, Math.max(0, Math.round(Number(contentAgeAheadYears) || 1)));
  return {
    contentAgeAheadYears: ahead,
    contentAgeMin: learnerAgeYears + ahead - 1,
    contentAgeMax: learnerAgeYears + ahead + 2,
  };
}

export function formatContentAgePromptBlock(
  learnerAgeYears: number,
  contentAgeAheadYears = 1,
): string {
  const range = resolveContentAgeRange(learnerAgeYears, contentAgeAheadYears);
  const ahead = range.contentAgeAheadYears;
  return [
    `【学习者实龄】约 ${learnerAgeYears} 岁（来自用户档案；仅作基准，不是目标难度）`,
    `【内容适配年龄区间】约 ${range.contentAgeMin}～${range.contentAgeMax} 岁（超前设定 +${ahead}；请按区间写题，不要钉死在刚好 ${learnerAgeYears} 岁）`,
    `【难度适配 · 硬约束】衍生题干、设问粒度、符号密度与表述必须落在约 ${range.contentAgeMin}～${range.contentAgeMax} 岁认知水平内。`,
    '禁止超出该区间的超纲难题或抽象堆叠：若成员题过难，应在保留共性考点的前提下改写到可理解的表述与步骤密度；'
      + '学习者看不懂题面等同失败。',
  ].join('\n');
}

function promptAlreadyHasLearnerAge(prompt: string): boolean {
  return prompt.includes('【难度适配')
    || prompt.includes('【内容适配年龄区间】')
    || prompt.includes('【学习者实龄】')
    || prompt.includes('【学习者年龄】');
}

export function ensureContentAgeInPrompt(
  prompt: string,
  ageBlock: string,
): string {
  const base = String(prompt || '').trim();
  const block = String(ageBlock || '').trim();
  if (!block) return base;
  if (!base) return block;
  if (promptAlreadyHasLearnerAge(base)) return base;
  return `${base}\n\n${block}`;
}

/** 读取当前用户档案与学习设置，拼出年龄约束块（无出生年则空） */
export async function loadContentAgePromptBlock(userId: string): Promise<string> {
  const normalized = String(userId || '').trim();
  if (!normalized) return '';
  try {
    const bundle = await accountUserProfileService.getProfileBundle(normalized);
    const age = bundle.profile?.derivedAgeYears;
    if (age == null || !Number.isFinite(age) || age < 0) return '';

    // 以内存学习设置为准（点击设置后立刻写入）；勿再拉远端覆盖，否则会把刚改的值盖回去
    let ahead = 1;
    try {
      const local = getLearningSequenceSettings(normalized);
      if (local.contentAgeAheadYears != null) {
        ahead = local.contentAgeAheadYears;
      }
    } catch {
      // 默认 +1
    }

    return formatContentAgePromptBlock(age, ahead);
  } catch {
    return '';
  }
}
